import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockContainer from '../../BlockBuilder/BlockContainer';

import SectionContainer from '../SectionContainer';

import css from './SectionEvents.module.css';

/**
 * Console-content convention for event metadata.
 *
 * The block's `text` field accepts optional `key: value` lines at the very top,
 * followed by a blank line, then the body prose. Parsed keys are rendered as
 * a styled metadata block above the body; unparsed text is passed through to
 * the markdown renderer unchanged.
 *
 * Recognized keys (all optional, render in this order):
 *   day       e.g. "Saturday"
 *   date      e.g. "May 16, 2026" — kept as free text; renderer doesn't parse
 *   time      e.g. "2:00 – 5:00 PM"
 *   location  e.g. "Covet Art Gallery, Oceanside"
 *
 * Operator example (typed into Console → block → Text):
 *
 *   day: Saturday
 *   date: May 16, 2026
 *   time: 2:00 – 5:00 PM
 *   location: Covet Art Gallery
 *
 *   Watch artist Margaret Chiaro bring her signature cloudscapes to life…
 *
 * Why frontmatter (and not a custom block field): Sharetribe's block schema
 * is fixed (title / text / media / callToAction). We can't add UI-driven
 * custom fields. Frontmatter inside the existing `text` field gives operators
 * a guided structure without a Console-platform change, and degrades cleanly —
 * cards without a frontmatter section render exactly as before.
 *
 * This deliberately crosses the "date-unaware code" rule established in
 * the events-section-direction memory; that decision was reopened in
 * conversation 2026-05-21.
 */
const FRONTMATTER_KEYS = ['day', 'date', 'time', 'location'];

const parseEventFrontmatter = raw => {
  if (typeof raw !== 'string' || raw.length === 0) {
    return { metadata: {}, body: raw || '' };
  }
  const lines = raw.split('\n');
  const metadata = {};
  let i = 0;
  // Skip leading blank lines — Console's markdown editor sometimes prepends
  // whitespace, and we don't want a stray newline to silently disable
  // frontmatter parsing.
  while (i < lines.length && lines[i].trim() === '') i++;

  for (; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') {
      i++;
      break;
    }
    const match = line.match(/^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.+)$/);
    if (!match) break;
    const key = match[1].toLowerCase();
    // Only recognized keys are consumed — anything else is left in the body so
    // operators can't accidentally hide content by typing a stray colon.
    if (!FRONTMATTER_KEYS.includes(key)) break;
    metadata[key] = match[2].trim();
  }
  if (Object.keys(metadata).length === 0) {
    // No frontmatter found — return the raw content unchanged, including any
    // leading whitespace, so the markdown renderer sees exactly what the
    // operator typed.
    return { metadata: {}, body: raw };
  }
  // Drop the consumed frontmatter lines (including any leading blanks we
  // skipped past) so they don't leak into the markdown body below.
  return { metadata, body: lines.slice(i).join('\n') };
};

const getTextContent = textField =>
  typeof textField === 'string' ? textField : textField?.content ?? '';

/**
 * Renders the parsed metadata as a small label/value group above the event body.
 * Day and date collapse onto a single "When" row when both are present so the
 * visual hierarchy stays compact.
 */
const EventMeta = ({ metadata }) => {
  const dayDate = [metadata.day, metadata.date].filter(Boolean).join(', ');
  const whenValue = [dayDate, metadata.time].filter(Boolean).join(' · ');
  const hasWhen = whenValue.length > 0;
  const hasWhere = !!metadata.location;
  if (!hasWhen && !hasWhere) return null;

  return (
    <dl className={css.eventMeta}>
      {hasWhen ? (
        <div className={css.eventMetaItem}>
          <dt className={css.eventMetaLabel}>When</dt>
          <dd className={css.eventMetaValue}>{whenValue}</dd>
        </div>
      ) : null}
      {hasWhere ? (
        <div className={css.eventMetaItem}>
          <dt className={css.eventMetaLabel}>Where</dt>
          <dd className={css.eventMetaValue}>{metadata.location}</dd>
        </div>
      ) : null}
    </dl>
  );
};

const SectionEvents = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {hasBlocks ? (
        <div
          className={classNames(defaultClasses.blockContainer, css.eventsMain, {
            [css.noSidePaddings]: isInsideContainer,
          })}
        >
          {blocks.map((block, i) => {
            // Pull the raw text content, strip the frontmatter (if any), and
            // reassemble the text field with the body-only content so the
            // markdown renderer doesn't show the raw `key: value` lines.
            const rawText = getTextContent(block.text);
            const { metadata, body } = parseEventFrontmatter(rawText);
            const hasMeta = Object.keys(metadata).length > 0;
            const bodyText = hasMeta ? { ...(block.text || {}), content: body } : block.text;
            const hasTextish = hasDataInFields(
              [block.title, bodyText, block.callToAction],
              fieldOptions
            );

            return (
              <BlockContainer
                key={block.blockId || block.blockName || `event-${i}`}
                id={block.blockId}
                className={css.block}
              >
                {block.media ? (
                  <div className={css.media}>
                    <Field
                      data={block.media}
                      sizes="(max-width: 767px) 100vw, 200px"
                      options={fieldOptions}
                    />
                  </div>
                ) : null}
                {hasTextish || hasMeta ? (
                  <div className={css.text}>
                    <Field data={block.title} options={fieldOptions} />
                    {hasMeta ? <EventMeta metadata={metadata} /> : null}
                    <Field data={bodyText} options={fieldOptions} />
                    <Field
                      data={block.callToAction}
                      className={css.ctaButton}
                      options={fieldOptions}
                    />
                  </div>
                ) : null}
              </BlockContainer>
            );
          })}
        </div>
      ) : null}
    </SectionContainer>
  );
};

export default SectionEvents;
